import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { DateTime } from 'luxon'
import { GET_PAST_GROUP_EVENTS, GET_UPCOMING_GROUP_EVENTS } from './query'
import { GROUP_EVENT_PAGE_LIMIT } from 'lib/pga-dot-coach'
import { CREATE_GROUP_EVENT_NEW_PATH } from './create-event-form/quick-start-visibility'
import { useFeatureFlags, flags } from 'lib/feature-flags'

const useGroupEventsQuery = (query, variables, skip) => {
  return useQuery(query, {
    variables,
    skip,
  })
}

const useGroupEventsPanel = ({ skip = false } = {}) => {
  const history = useHistory()
  const [selectedTab, setSelectedTab] = useState('upcoming')
  const [showCanceledEvents, setShowCanceledEvents] = useState(false)
  const [offset, setOffset] = useState(0)
  const [showLimitReachedDisclaimer, setShowLimitReachedDisclaimer] = useState(
    false,
  )
  const [openFreeTierLimitDialog, setOpenFreeTierLimitDialog] = useState(false)
  const [showCreateEventQuickStart, setShowCreateEventQuickStart] = useState(
    false,
  )
  const [isPgaHopeCoach, setIsPgaHopeCoach] = useState(false)
  const [withRecurrence] = useFeatureFlags([
    flags.FLAG_FEAT_GROUP_EVENT_RECURRING_INSTANCES,
  ])

  useEffect(() => {
    setOffset(0)
  }, [selectedTab, showCanceledEvents])

  const showPastEvents = selectedTab === 'past'
  const startDateTime = showPastEvents
    ? {
      from: null,
      to: DateTime.local()
        .startOf('day')
        .toISO(),
    }
    : {
      from: DateTime.now()
        .startOf('day')
        .toISO(),
      to: null,
    }

  const pastEventsVariables = {
    orderBy: { field: 'START_DATE_TIME', direction: 'DESC' },
    startDateTime,
    includeCanceled: showCanceledEvents,
    limit: GROUP_EVENT_PAGE_LIMIT,
    offset,
    withRecurrence: !!withRecurrence,
    skip: selectedTab !== 'past',
  }

  const upcomingEventsVariables = {
    orderBy: { field: 'START_DATE_TIME', direction: 'ASC' },
    startDateTime,
    includeCanceled: showCanceledEvents,
    limit: GROUP_EVENT_PAGE_LIMIT,
    offset,
    withRecurrence: !!withRecurrence,
    skip: selectedTab === 'past',
  }

  const pastEventsResult = useGroupEventsQuery(
    GET_PAST_GROUP_EVENTS,
    pastEventsVariables,
    skip || selectedTab !== 'past',
  )
  const upcomingEventsResult = useGroupEventsQuery(
    GET_UPCOMING_GROUP_EVENTS,
    upcomingEventsVariables,
    skip || selectedTab === 'past',
  )

  const queryResult =
    selectedTab === 'past' ? pastEventsResult : upcomingEventsResult
  const { data, loading, error, refetch } = queryResult

  const handleCreateEventClick = () => {
    if (showLimitReachedDisclaimer) {
      setOpenFreeTierLimitDialog(true)
      return
    }
    setShowCreateEventQuickStart(true)
  }

  const handleCreateEventQuickStartCancel = () => {
    setShowCreateEventQuickStart(false)
  }

  const handleCreateEventQuickStartContinue = () => {
    setShowCreateEventQuickStart(false)
    history.push(CREATE_GROUP_EVENT_NEW_PATH, { skipQuickStart: true })
  }

  const handleCreateEventQuickStartSourceSelected = () => {
    setShowCreateEventQuickStart(false)
  }

  return {
    selectedTab,
    setSelectedTab,
    showCanceledEvents,
    setShowCanceledEvents,
    offset,
    setOffset,
    showLimitReachedDisclaimer,
    setShowLimitReachedDisclaimer,
    openFreeTierLimitDialog,
    setOpenFreeTierLimitDialog,
    showCreateEventQuickStart,
    handleCreateEventQuickStartCancel,
    handleCreateEventQuickStartContinue,
    handleCreateEventQuickStartSourceSelected,
    isPgaHopeCoach,
    setIsPgaHopeCoach,
    data,
    loading,
    error,
    refetch,
    handleCreateEventClick,
  }
}

export default useGroupEventsPanel
